import { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  DatePicker,
  Select,
  Input,
  Tag,
  Popconfirm,
  message,
  Form,
  Alert,
  Empty,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listEmployeeStatusPeriods,
  createEmployeeStatusPeriod,
  updateEmployeeStatusPeriod,
  deleteEmployeeStatusPeriod,
} from '@/services/payrollEngine.service';

const STATUS_OPTIONS = [
  { value: '010', label: '010 — Active', color: 'green' },
  { value: '020', label: '020 — Probation', color: 'cyan' },
  { value: '030', label: '030 — Long sick (30+)', color: 'orange' },
  { value: '033', label: '033 — NMB only', color: 'gold' },
  { value: '040', label: '040 — Maternity', color: 'magenta' },
  { value: '050', label: '050 — Unpaid leave', color: 'red' },
  { value: '060', label: '060 — Pregnancy 30+', color: 'volcano' },
];

const SOURCE_COLOR = {
  inferred: 'default',
  manual: 'blue',
  request: 'purple',
};

function statusTag(status) {
  const opt = STATUS_OPTIONS.find((o) => o.value === status);
  return <Tag color={opt?.color || 'default'}>{opt?.label || status}</Tag>;
}

export default function StatusTimelineModal({
  open,
  onClose,
  employeeId,
  companyId,
  employeeName,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | record | {_new:true}
  const [form] = Form.useForm();

  const reload = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listEmployeeStatusPeriods(employeeId);
      setItems(result?.items || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (open) reload();
  }, [open, reload]);

  const openCreate = () => {
    setEditing({ _new: true });
    form.resetFields();
    form.setFieldsValue({
      status: '040',
      source: 'manual',
      fromDate: dayjs(),
    });
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue({
      status: record.status,
      source: record.source || 'manual',
      fromDate: record.fromDate ? dayjs(record.fromDate) : null,
      toDate: record.toDate ? dayjs(record.toDate) : null,
      notes: record.notes || '',
    });
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload = {
      employee: employeeId,
      company: companyId,
      status: values.status,
      source: values.source,
      fromDate: values.fromDate?.toISOString(),
      toDate: values.toDate ? values.toDate.toISOString() : null,
      notes: values.notes,
    };
    try {
      if (editing?._id) {
        await updateEmployeeStatusPeriod(editing._id, payload);
        message.success('Updated');
      } else {
        await createEmployeeStatusPeriod(payload);
        message.success('Created');
      }
      setEditing(null);
      reload();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEmployeeStatusPeriod(id);
      message.success('Deleted');
      reload();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'fromDate',
      dataIndex: 'fromDate',
      width: 110,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'toDate',
      dataIndex: 'toDate',
      width: 110,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'status',
      dataIndex: 'status',
      width: 200,
      render: (v) => statusTag(v),
    },
    {
      title: 'source',
      dataIndex: 'source',
      width: 90,
      render: (v) => <Tag color={SOURCE_COLOR[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'notes',
      dataIndex: 'notes',
      ellipsis: true,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this period?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      title={`Status timeline — ${employeeName || ''}`}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {error ? (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 12 }}
          closable
          onClose={() => setError(null)}
        />
      ) : null}
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add period
        </Button>
        <Button icon={<ReloadOutlined />} onClick={reload}>
          Reload
        </Button>
      </Space>
      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={false}
        locale={{ emptyText: <Empty description="No status periods" /> }}
      />

      <Modal
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={handleSave}
        title={editing?._new ? 'Create status period' : 'Edit status period'}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="fromDate"
            label="From date"
            rules={[{ required: true, message: 'From date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="toDate" label="To date (optional, open-ended if blank)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="source"
            label="Source"
            initialValue="manual"
          >
            <Select
              options={[
                { value: 'manual', label: 'manual (HR entered)' },
                { value: 'inferred', label: 'inferred (from salary docs)' },
                { value: 'request', label: 'request (from leave request)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}
